import { fromJS } from "immutable";

import { withMutations } from "@/redux/modules/helpers/reducerWrappers";
import { sumTotalAreaOfSquareFeetInSquareMeters } from "@/utils/measurement/measurement-aggregate";

/**
 * Action type for adding a square feet length. Handled by this wrapper so we don't add
 * new logic to the large additional-products.js module.
 */
export const ADD_SQUARE_FEET_LENGTH = "ADD_SQUARE_FEET_LENGTH";
export const DELETE_SQUARE_FEET_LENGTH = "DELETE_SQUARE_FEET_LENGTH";

const getPathConfig = (value) => {
  const { type, itemIndex, cartIndex, subKitIndex } = value;
  if (type === "subKitItem") {
    return {
      basePath: ["items", itemIndex, "subKits", subKitIndex, "kitProducts", cartIndex],
      estimatedKey: "lengthItems",
      hasInitialLengthItems: true,
    };
  }
  if (type === "kitItem") {
    return {
      basePath: ["items", itemIndex, "productKitItems", cartIndex],
      estimatedKey: "lengthItems",
      hasInitialLengthItems: true,
    };
  }
  return {
    basePath: ["items", itemIndex],
    estimatedKey: "subitems",
    hasInitialLengthItems: false,
  };
};

const addSquareFeetDimensionLogic = (_state, value) => {
  const {
    isActual,
    totalAreaInSquareFeet,
    totalAreaInSquareMeters,
    amount,
    length,
    width,
    lengthFt,
    lengthIn,
    widthFt,
    widthIn,
  } = value;
  const { basePath, estimatedKey, hasInitialLengthItems } = getPathConfig(value);

  const existingItems = _state
    .getIn([...basePath, !isActual ? estimatedKey : "actualLengthItems"], fromJS([]))
    .toJS();
  const items = [
    ...existingItems,
    {
      totalAreaInSquareFeet,
      totalAreaInSquareMeters,
      amount,
      length,
      width,
      lengthFt,
      lengthIn,
      widthFt,
      widthIn,
    },
  ];
  const { total: totalSqm } = sumTotalAreaOfSquareFeetInSquareMeters({ items });
  const immutableItems = fromJS(items);
  const sumObject = fromJS({ total: parseFloat(totalSqm) });

  if (!isActual) {
    _state.setIn([...basePath, estimatedKey], immutableItems);
    if (hasInitialLengthItems) {
      _state.setIn([...basePath, "initialLengthItems"], immutableItems);
    }
    _state.setIn([...basePath, "sumLengths"], sumObject);
  }

  _state.setIn([...basePath, "actualLengthItems"], immutableItems);
  _state.setIn([...basePath, "actualSumLengths"], sumObject);
};

const deleteSquareFeetDimensionLogic = (_state, value) => {
  const { subitemIndex, isActual } = value;
  const { basePath, estimatedKey, hasInitialLengthItems } = getPathConfig(value);

  if (!isActual) {
    _state.deleteIn([...basePath, estimatedKey, subitemIndex]);
    if (hasInitialLengthItems) {
      _state.deleteIn([...basePath, "initialLengthItems", subitemIndex]);
    }
  }

  _state.deleteIn([...basePath, "actualLengthItems", subitemIndex]);

  const items = _state
    .getIn([...basePath, !isActual ? estimatedKey : "actualLengthItems"], fromJS([]))
    .toJS();
  const { total: totalSqm } = sumTotalAreaOfSquareFeetInSquareMeters({ items });
  const sumObject = fromJS({ total: parseFloat(totalSqm) });

  if (!isActual) {
    _state.setIn([...basePath, "sumLengths"], sumObject);
  }

  _state.setIn([...basePath, "actualSumLengths"], sumObject);
};

const addSquareFeetDimensionReducer = withMutations(addSquareFeetDimensionLogic);
const deleteSquareFeetDimensionReducer = withMutations(deleteSquareFeetDimensionLogic);

/**
 * Wraps the original additional-products reducer so that ADD_SQUARE_FEET_LENGTH
 * is handled here instead of in additional-products.js. All other actions are delegated.
 *
 * @param {Function} originalReducer - additionalProducts.reducers
 * @returns {Function} Wrapped reducer for the additionalProducts slice
 */
const createAdditionalProductsWrapper = (originalReducer) => (state, action) => {
  if (action.type === ADD_SQUARE_FEET_LENGTH) {
    return addSquareFeetDimensionReducer(state, action);
  }
  if (action.type === DELETE_SQUARE_FEET_LENGTH) {
    return deleteSquareFeetDimensionReducer(state, action);
  }
  return originalReducer(state, action);
};

export default createAdditionalProductsWrapper;
